import { useMemo, useCallback } from 'react';
import { ArrowLeftSolidIcon, ArrowRightSolidIcon } from '@navi/web-ui/lib/icons';
import { Button, Typography } from '@navi/web-ui/lib/primitives';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { RootState } from 'src/store';
import styles from '../index.module.scss';
import { interpolatePathParams } from 'src/utils/interpolate';
import APP_ROUTES from 'src/layout/Routes';
import {
  fetchCaseRecommendationNextCase,
  fetchCaseRecommendationPreviousCase
} from 'src/pages/CaseRecommendation/caseRecommendationActions';
import cx from 'classnames';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';
import { CLICKSTREAM_EVENT_NAMES } from '@cp/src/service/clickStream.constant';
import { TAB_KEYS } from '@cp/src/pages/CaseDetails/constants';
import { navigateToCase } from '@cp/src/pages/CaseRecommendation/utils';

const NextPreviousCaseActionsRecommended = () => {
  const { loanId = '', customerId = '', caseReferenceId = '' } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const currentRecommendedCase = useSelector(
    (state: RootState) => state.caseRecommendation.currentRecommendedCase
  );

  const isCurrentlyOnRecommendedCase = useMemo(() => {
    if (!currentRecommendedCase?.caseReferenceId) return false;

    return (
      currentRecommendedCase.entityId === loanId &&
      currentRecommendedCase.customerReferenceId === customerId &&
      currentRecommendedCase.caseReferenceId === caseReferenceId
    );
  }, [currentRecommendedCase, loanId, customerId, caseReferenceId]);

  const hasRecommendedCaseContext = Boolean(currentRecommendedCase?.customerReferenceId !== '');

  const onGoToPreviousCase = useCallback(() => {
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_CASE_RECOMMENDATION_NAVIGATION_PREV_CASE_CLICK);

    dispatch(fetchCaseRecommendationPreviousCase(navigateToCase));
  }, []);

  const onGoToNextCase = useCallback(() => {
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_CASE_RECOMMENDATION_NAVIGATION_NEXT_CASE_CLICK);

    dispatch(fetchCaseRecommendationNextCase(navigateToCase));
  }, []);

  const onGoBackToRecommendedCases = () => {
    addClickstreamEvent(
      CLICKSTREAM_EVENT_NAMES.LH_CASE_RECOMMENDATION_NAVIGATION_BACK_TO_RECOMMENDED_CASES_CLICK
    );
    navigate(
      interpolatePathParams(APP_ROUTES.CASE_DETAIL.path, {
        selectedTab: TAB_KEYS.OVERVIEW,
        loanId: currentRecommendedCase.entityId,
        customerId: currentRecommendedCase.customerReferenceId,
        caseReferenceId: currentRecommendedCase.caseReferenceId || '',
        caseBusinessVertical: currentRecommendedCase.businessVertical || ''
      })
    );
  };

  if (isCurrentlyOnRecommendedCase && hasRecommendedCaseContext) {
    return (
      <>
        <div className={styles.goToNextCaseContainer}>
          <Typography variant="p3" className={styles.nextCaseTextHeader}>
            Call {currentRecommendedCase.priority} of {currentRecommendedCase.totalCases}
          </Typography>
          <div className="flex items-center gap-3">
            <Button
              variant="secondary"
              size="small"
              onClick={onGoToPreviousCase}
              startAdornment={<ArrowLeftSolidIcon />}
              disabled={currentRecommendedCase.priority === 1}
            >
              Prev
            </Button>
            <Button
              variant="secondary"
              size="small"
              onClick={onGoToNextCase}
              endAdornment={<ArrowRightSolidIcon />}
              disabled={currentRecommendedCase.priority === currentRecommendedCase.totalCases}
            >
              Next
            </Button>
          </div>
        </div>
      </>
    );
  }

  if (hasRecommendedCaseContext && !isCurrentlyOnRecommendedCase) {
    return (
      <>
        <div className={cx(styles.goToNextCaseContainer, 'justify-between')}>
          <Typography variant="p3" className={styles.nextCaseTextHeader}>
            Go back to recommended cases
          </Typography>
          <Button
            variant="secondary"
            className={styles.nextCaseButton}
            size="small"
            onClick={onGoBackToRecommendedCases}
            startAdornment={<ArrowLeftSolidIcon />}
          >
            Go back
          </Button>
        </div>
      </>
    );
  }

  return null;
};

export default NextPreviousCaseActionsRecommended;
